import { useInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { type Columns, DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Select } from "@/components/ui/field";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { sessionQuery } from "@/features/auth/queries";
import { useSession } from "@/features/auth/useSession";
import { directoryQuery, nameFor } from "@/features/directory/queries";
import { masterTypeIndexQuery } from "@/features/master-types/queries";
import type { SubmissionListItem, SubmissionScope } from "@/features/submissions/api";
import { QueueAge } from "@/features/submissions/components/QueueAge";
import { submissionsQuery } from "@/features/submissions/queries";

/**
 * The reviewer queue — where a submission becomes findable.
 *
 * Until PR6 there was no route to one at all. Every submission endpoint
 * took an id the caller had to already hold, and an id was returned
 * exactly once, to whoever created it, so a reviewer who was not the
 * submitter had no path to their own work; PR5's smoke test needed a
 * database query to get one.
 *
 * **The server decides which queue this is.** `scope` is omitted on first
 * load and the response reports which one it chose — `mine` for anybody
 * who can approve something, `submitted` for everybody else. Defaulting
 * client-side would mean guessing from `can()` and landing a creator on a
 * permanently empty reviewer queue.
 */
export const Route = createFileRoute("/_authed/submissions/")({
  staticData: { crumbs: [{ key: "nav.submissions" }] },
  // `?kind=schema|record`, so the dashboard's approvals summary can link
  // straight into the queue pre-filtered rather than landing on "any
  // kind" and making the reviewer redo the filter it already knew.
  validateSearch: (search: Record<string, unknown>): { kind?: "schema" | "record" } => {
    const kind = search.kind;
    return kind === "schema" || kind === "record" ? { kind } : {};
  },
  loader: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(sessionQuery);
    // The queue itself is not prefetched: it is an infinite query, and the
    // DataTable's six named states are what cover its loading. Only the two
    // lookups every row needs are primed, so no row renders a uuid while a
    // second request is still deciding what to call it.
    await Promise.all([
      context.queryClient.ensureQueryData(masterTypeIndexQuery(me.resolved_locale)),
      context.queryClient.ensureQueryData(directoryQuery()),
    ]);
  },
  component: SubmissionQueue,
});

const SCOPES: SubmissionScope[] = ["mine", "submitted", "all"];

function SubmissionQueue() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me, canAnywhere } = useSession();
  const locale = me.resolved_locale;
  const search = Route.useSearch();

  const { data: index } = useSuspenseQuery(masterTypeIndexQuery(locale));
  const { data: directory } = useSuspenseQuery(directoryQuery());

  // `undefined` until the reviewer picks a tab, so the first render uses
  // the server's own choice rather than overriding it.
  const [scope, setScope] = useState<SubmissionScope | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>("pending");
  const [kind, setKind] = useState<string | undefined>(search.kind);

  const query = useInfiniteQuery(submissionsQuery({ scope, status, kind }));
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);
  const resolvedScope = query.data?.pages[0]?.scope ?? scope ?? "mine";

  // Only offered to somebody it will work for: asking for `all` without
  // tenant-wide standing is a 403 with its own problem type, not a silent
  // narrowing, so showing the tab to everyone would produce an error that
  // looks like a bug.
  const maySeeAll = canAnywhere("handover.decide") || canAnywhere("record.approve");

  // Built inside the memo rather than beside it: a helper defined in the
  // component body is a new function every render, so it would have to be
  // a dependency and the memo would never hold.
  const defaultLocale = me.tenant.default_locale;

  const columns = useMemo<Columns<SubmissionListItem>>(
    () => [
      {
        id: "master_type_id",
        accessorFn: (row: SubmissionListItem) => {
          const type = index.byId.get(row.master_type_id);
          if (type === undefined) return row.master_type_id;
          return type.display_name[locale] ?? type.display_name[defaultLocale] ?? type.code;
        },
        header: t("submissions.columns.type"),
      },
      {
        id: "subject",
        accessorFn: (row: SubmissionListItem) => row.subject ?? "",
        header: t("submissions.columns.subject"),
        cell: ({ row }) => (
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-foreground">{row.original.subject ?? "—"}</span>
            {/* A cascade is a different decision from a single change,
                and this is the only place that is visible before the
                submission is opened. */}
            {row.original.item_count > 1 && (
              <span className="rounded-control bg-warning-surface px-1.5 py-0.5 text-xs text-warning">
                {t("submissions.itemCount", { count: row.original.item_count })}
              </span>
            )}
          </span>
        ),
      },
      {
        id: "action",
        accessorFn: (row: SubmissionListItem) => `${row.kind}/${row.action}`,
        header: t("submissions.columns.change"),
        cell: ({ row }) => (
          <span className="flex flex-col">
            <span className="text-foreground">
              {t(`submissions.action.${row.original.action}`)}
            </span>
            <span className="text-xs text-muted">{t(`submissions.kind.${row.original.kind}`)}</span>
          </span>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: t("submissions.columns.status"),
        cell: ({ row }) => <StatusBadge status={statusOf(row.original)} />,
      },
      {
        id: "submitted_by",
        accessorFn: (row: SubmissionListItem) => nameFor(directory, row.submitted_by) ?? "",
        header: t("submissions.columns.submittedBy"),
      },
      {
        id: "reviewer_id",
        accessorFn: (row: SubmissionListItem) => nameFor(directory, row.reviewer_id) ?? "",
        header: t("submissions.columns.reviewer"),
        cell: ({ row }) =>
          row.original.reviewer_id === null ? (
            // Not a blank: an unassigned submission is decidable by any
            // approver, which is a fact worth stating rather than an
            // absence to be inferred.
            <span className="text-muted text-xs">{t("submissions.unassigned")}</span>
          ) : (
            (nameFor(directory, row.original.reviewer_id) ?? "")
          ),
      },
      {
        id: "submitted_at",
        accessorKey: "submitted_at",
        header: t("submissions.columns.waiting"),
        cell: ({ row }) => <QueueAge submittedAt={row.original.submitted_at} />,
      },
    ],
    [t, directory, index, locale, defaultLocale],
  );

  return (
    <>
      <PageHeader
        title={t("submissions.title")}
        description={t("submissions.description")}
        help={t("pageHelp.submissions")}
      />

      {/*
        A real tablist, replacing the `aria-pressed` button row: three
        views of one queue is what a tablist means, and Radix brings the
        arrow-key movement between them that the old fieldset never had.

        `value` is `resolvedScope`, not `scope` — **the server decides
        which queue this is on first load** and reports back which one it
        chose. Binding the tab to the client's `scope` would light up
        "Waiting on me" while the server was actually serving "I
        submitted", which is the one thing this screen has always been
        careful about.

        One `TabsContent`, its value also `resolvedScope`, so it always
        matches the active tab. Three panels holding the same table would
        mount the same query three times to show it once.
      */}
      <Tabs value={resolvedScope} onValueChange={(next) => setScope(next as SubmissionScope)}>
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <TabsList variant="underline" aria-label={t("submissions.scope.label")}>
            {SCOPES.filter((option) => option !== "all" || maySeeAll).map((option) => (
              <TabsTrigger
                key={option}
                variant="underline"
                value={option}
                tooltip={t(`submissions.scope.about.${option}`)}
              >
                {t(`submissions.scope.${option}`)}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* `htmlFor`/`id` rather than wrapping the control in the
              label. Wrapping worked while these were raw `<select>`s, but
              the shared `Select` is a component, and neither biome's
              `noLabelWithoutControl` nor a reader can see a control
              through it — an explicit association is what actually
              survives the indirection. */}
          <div className="ml-auto flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 text-sm">
              <label className="text-muted" htmlFor="submission-status-filter">
                {t("submissions.filter.status")}
              </label>
              <Select
                tooltip={t("tooltips.statusFilter")}
                id="submission-status-filter"
                value={status ?? ""}
                onChange={(event) =>
                  setStatus(event.target.value === "" ? undefined : event.target.value)
                }
                className="w-auto"
              >
                <option value="">{t("submissions.filter.any")}</option>
                {/* The mock's fifth status, "Changes requested", is not
                    offered: this workflow has no such state and it is
                    designer fill. */}
                {["pending", "approved", "rejected"].map((option) => (
                  <option key={option} value={option}>
                    {t(`status.${option}`)}
                  </option>
                ))}
              </Select>
            </span>

            <span className="inline-flex items-center gap-2 text-sm">
              <label className="text-muted" htmlFor="submission-kind-filter">
                {t("submissions.filter.kind")}
              </label>
              <Select
                tooltip={t("tooltips.kindFilter")}
                id="submission-kind-filter"
                value={kind ?? ""}
                onChange={(event) =>
                  setKind(event.target.value === "" ? undefined : event.target.value)
                }
                className="w-auto"
              >
                <option value="">{t("submissions.filter.any")}</option>
                {["record", "schema"].map((option) => (
                  <option key={option} value={option}>
                    {t(`submissions.kind.${option}`)}
                  </option>
                ))}
              </Select>
            </span>
          </div>
        </div>

        <TabsContent value={resolvedScope}>
          <DataTable
            caption={t("submissions.title")}
            columns={columns}
            data={rows}
            getRowId={(row) => row.id}
            isLoading={query.isPending}
            isFetching={query.isFetching && !query.isFetchingNextPage}
            error={query.error}
            onRetry={() => void query.refetch()}
            hasMore={query.hasNextPage}
            isLoadingMore={query.isFetchingNextPage}
            onLoadMore={() => void query.fetchNextPage()}
            onRowActivate={(row) =>
              void navigate({ to: "/submissions/$submissionId", params: { submissionId: row.id } })
            }
            emptyTitle={t(`submissions.empty.${resolvedScope}.title`)}
            emptyBody={t(`submissions.empty.${resolvedScope}.body`)}
          />
        </TabsContent>
      </Tabs>
    </>
  );
}

/** A submission's own lifecycle, which is not a record's. `draft` exists
 *  in the CHECK constraint but nothing creates one today. */
function statusOf(
  row: SubmissionListItem,
): "draft" | "pending" | "approved" | "rejected" | "cancelled" {
  if (row.status === "pending") return "pending";
  if (row.status === "approved") return "approved";
  if (row.status === "rejected") return "rejected";
  if (row.status === "cancelled") return "cancelled";
  return "draft";
}
